/**
 * StructuredFitPanel — Company Detail Page tile (Feb 2026 · AI Mapping v2).
 *
 * Renders the rolled-up "Structured Fit Profile" for a pharma company
 * along with a live 4-tier asset-fit counter (Strong / Good / Weak /
 * Blocked). Clicking a tier opens a filtered asset drill-down modal
 * with per-asset explainability chips.
 *
 * Data source:
 *   GET /api/companies/{name}/effective-profile
 *   GET /api/companies/{name}/structured-fit
 *   GET /api/companies/{name}/structured-fit/export?tier=blocked
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Target, Ban, Sparkles, Layers, Users,
  ChevronDown, ChevronRight, Download, ShieldCheck, ShieldAlert,
  Wand2, Mail,
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELD_META = [
  { key: 'interest_areas',            label: 'Interest Areas',         Icon: Layers,       chip: 'bg-violet-50 text-violet-700 border-violet-200' },
  { key: 'therapeutic_areas_specific', label: 'Specific Indications',  Icon: Target,       chip: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  { key: 'priority_targets_moas',      label: 'Priority Targets / MOAs', Icon: Target,     chip: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200' },
  { key: 'preferred_modalities',       label: 'Preferred Modalities',  Icon: ShieldCheck,  chip: 'bg-sky-50 text-sky-700 border-sky-200' },
  { key: 'excluded_modalities',        label: 'Excluded Modalities',   Icon: Ban,          chip: 'bg-rose-50 text-rose-700 border-rose-200' },
  { key: 'excluded_areas',             label: 'Excluded Areas',        Icon: Ban,          chip: 'bg-rose-50 text-rose-700 border-rose-200' },
  { key: 'excluded_indications',       label: 'Excluded Indications',  Icon: Ban,          chip: 'bg-rose-50 text-rose-700 border-rose-200' },
  { key: 'scientific_preferences',     label: 'Scientific Preferences', Icon: Sparkles,    chip: 'bg-teal-50 text-teal-700 border-teal-200' },
  { key: 'focus_notes',                label: 'Focus Notes',           Icon: Sparkles,     chip: 'bg-slate-50 text-slate-700 border-slate-300' },
  { key: 'preferred_stages',           label: 'Preferred Stages',      Icon: ShieldCheck,  chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { key: 'geographic_interests',       label: 'Geographies',           Icon: ShieldCheck,  chip: 'bg-amber-50 text-amber-700 border-amber-200' },
];

const TIER_META = {
  strong:  { label: 'Strong Fit', chip: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  good:    { label: 'Good Fit',   chip: 'bg-sky-100 text-sky-800 border-sky-300' },
  weak:    { label: 'Weak',       chip: 'bg-slate-100 text-slate-700 border-slate-300' },
  blocked: { label: 'Blocked',    chip: 'bg-rose-100 text-rose-800 border-rose-300' },
};

const encode = (s) => encodeURIComponent(s || '');

export const StructuredFitPanel = ({ companyName }) => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [counts, setCounts] = useState({ strong: 0, good: 0, weak: 0, blocked: 0, total: 0 });
  const [haveSignal, setHaveSignal] = useState(false);
  const [expandedField, setExpandedField] = useState(null);
  const [drillTier, setDrillTier] = useState(null);
  const [drillAssets, setDrillAssets] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [dedupeRunning, setDedupeRunning] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/companies/${encode(companyName)}/structured-fit?include_assets=false`, { withCredentials: true });
      setProfile(r.data.profile);
      setCounts(r.data.counts);
      setHaveSignal(r.data.have_any_signal);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load structured fit');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!companyName) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/api/companies/${encode(companyName)}/structured-fit?include_assets=false`, { withCredentials: true });
        if (cancelled) return;
        setProfile(r.data.profile);
        setCounts(r.data.counts);
        setHaveSignal(r.data.have_any_signal);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load structured fit');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyName]);

  const fields = profile?.fields || {};
  const sources = profile?.sources || {};

  const visibleFields = useMemo(
    () => FIELD_META.filter((m) => (fields[m.key] || []).length > 0),
    [fields],
  );

  const openTier = async (tier) => {
    setDrillTier(tier);
    setDrillLoading(true);
    setDrillAssets([]);
    try {
      const r = await axios.get(
        `${API}/api/companies/${encode(companyName)}/structured-fit?tier=${tier}&limit=500`,
        { withCredentials: true },
      );
      setDrillAssets(r.data.assets || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load tier assets');
    } finally {
      setDrillLoading(false);
    }
  };

  const exportBlocked = () => {
    const url = `${API}/api/companies/${encode(companyName)}/structured-fit/export?tier=blocked`;
    window.open(url, '_blank');
  };

  // Aug 2026 · Dedupe / canonicalize the Interest Areas list. Collapses
  // "CNS / Neurology / Psychiatry / Mental and behavioural disorders"
  // (13+ variants) into their canonical bucket ("Neuroscience") and
  // stashes the original raw strings under interest_area_aliases so
  // nothing is silently lost.
  const runDedupe = async () => {
    if (!profile?.company_profile_id) {
      toast.error('Company profile id missing');
      return;
    }
    setDedupeRunning(true);
    try {
      const r = await axios.post(
        `${API}/api/pharma-enrich/company_profile/${profile.company_profile_id}/canonicalize-interest-areas?apply=true`,
        {},
        { withCredentials: true },
      );
      toast.success(
        r.data.removed > 0
          ? `Collapsed ${r.data.before_count} tags into ${r.data.after_count} canonical areas`
          : 'Interest areas already canonical',
      );
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dedupe failed');
    } finally {
      setDedupeRunning(false);
    }
  };

  const aliases = profile?.interest_area_aliases || {};
  const hasAliasBloat = Object.values(aliases).some((v) => Array.isArray(v) && v.length > 0)
    || (profile?.fields?.interest_areas || []).length > 15;

  if (loading) {
    return (
      <Card className="border border-slate-200 shadow-sm" data-testid="structured-fit-panel">
        <CardContent className="p-5 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading Structured Fit Profile…
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border border-slate-200 shadow-sm" data-testid="structured-fit-panel">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-indigo-500" />
              <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Asset Fit Intelligence
              </h3>
              <Badge variant="secondary" className="text-[10px]" data-testid="structured-fit-contact-count">
                {profile?.contact_count || 0} contact{profile?.contact_count === 1 ? '' : 's'}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              {hasAliasBloat && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px] gap-1"
                  onClick={runDedupe}
                  disabled={dedupeRunning}
                  data-testid="dedupe-interest-areas-btn"
                  title="Collapse duplicate TA variants (CNS / Neurology / Psychiatry) into canonical buckets"
                >
                  {dedupeRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                  Dedupe Interest Areas
                </Button>
              )}
              <span className="text-[11px] text-slate-400">auto-rolled up from contacts + Deep Research</span>
            </div>
          </div>

          {/* Tier counters */}
          {haveSignal ? (
            <div className="grid grid-cols-4 gap-2" data-testid="fit-tier-counters">
              {(['strong', 'good', 'weak', 'blocked']).map((t) => (
                <button
                  key={t}
                  onClick={() => openTier(t)}
                  className={`text-left rounded-lg border px-3 py-2 hover:shadow-md transition ${TIER_META[t].chip}`}
                  data-testid={`fit-tier-${t}`}
                >
                  <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">
                    {TIER_META[t].label}
                  </div>
                  <div className="text-2xl font-bold">{counts[t] || 0}</div>
                </button>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 text-xs text-slate-600 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-500" />
              No structured signals yet — add Known Interests to contacts or run Deep Research to unlock ranked asset fit.
            </div>
          )}

          {/* Structured fields (expandable to show source attribution) */}
          {visibleFields.length > 0 ? (
            <div className="space-y-1.5 pt-2 border-t border-slate-100">
              {visibleFields.map(({ key, label, Icon, chip }) => {
                const values = fields[key] || [];
                const isOpen = expandedField === key;
                const src = sources[key] || [];
                // Aug 2026 · Show which raw deck variants were collapsed
                // into each canonical Interest Area (so BD lead sees
                // "Neuroscience ← CNS, Neurology, Psychiatry, ...").
                const aliasEntries = key === 'interest_areas'
                  ? values
                      .map((v) => [v, (aliases[v] || [])])
                      .filter(([, a]) => a.length > 0)
                  : [];
                return (
                  <div key={key} data-testid={`fit-field-${key}`}>
                    <button
                      onClick={() => setExpandedField(isOpen ? null : key)}
                      className="w-full flex items-start gap-2 text-left py-1 group"
                    >
                      {isOpen ? <ChevronDown className="w-3 h-3 mt-1.5 text-slate-400" /> : <ChevronRight className="w-3 h-3 mt-1.5 text-slate-400" />}
                      <Icon className="w-3.5 h-3.5 mt-1 text-slate-500" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">{label}</span>
                          <Badge variant="secondary" className="text-[9px] px-1.5 py-0">{values.length}</Badge>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {values.map((v, i) => (
                            <Badge key={i} className={`text-[11px] px-2 py-0.5 border ${chip}`}>{v}</Badge>
                          ))}
                        </div>
                      </div>
                    </button>
                    {isOpen && src.length > 0 && (
                      <div className="ml-9 mt-1 pl-2 border-l-2 border-slate-100 space-y-1">
                        {src.map((s, i) => (
                          <div key={i} className="text-[10.5px] text-slate-500">
                            {s.kind === 'contact' && <Users className="w-2.5 h-2.5 inline mr-1" />}
                            <span className="font-medium">{s.name}</span>
                            <span className="text-slate-400"> ({s.kind}) → </span>
                            <span>{s.values.join(', ')}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {isOpen && aliasEntries.length > 0 && (
                      <div className="ml-9 mt-1 pl-2 border-l-2 border-violet-100 space-y-1">
                        <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                          Collapsed variants
                        </div>
                        {aliasEntries.map(([canon, raws]) => (
                          <div key={canon} className="text-[10.5px] text-slate-500">
                            <span className="font-medium text-slate-600">{canon}</span>
                            <span className="text-slate-400"> ← </span>
                            <span>{raws.join(', ')}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : haveSignal ? null : null}

          {/* Aug 2026 · BD Contacts (From Deck) section REMOVED — people
              extracted from documents now flow into the Employees master
              records via entity resolution (see pharma_enrich.py). */}
        </CardContent>
      </Card>

      {/* Tier drill-down modal */}
      <Dialog open={!!drillTier} onOpenChange={(open) => !open && setDrillTier(null)}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto" data-testid="fit-tier-drill">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {drillTier && (
                <Badge className={`text-xs px-2.5 py-1 border ${TIER_META[drillTier]?.chip}`}>
                  {TIER_META[drillTier]?.label}
                </Badge>
              )}
              <span>· {counts[drillTier || 'strong'] || 0} assets</span>
              {drillTier === 'blocked' && (
                <Button size="sm" variant="outline" className="ml-auto text-xs gap-1" onClick={exportBlocked} data-testid="fit-tier-export">
                  <Download className="w-3 h-3" /> Export CSV
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {drillLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 p-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : drillAssets.length === 0 ? (
            <p className="text-sm text-slate-500 p-6">No assets in this tier.</p>
          ) : (
            <div className="space-y-2">
              {drillAssets.map((a, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3 hover:bg-slate-50" data-testid={`fit-asset-row-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-slate-800">{a.asset.asset_name || 'Untitled asset'}</span>
                        <Badge variant="outline" className="text-[10px]">{a.asset.biotech_company_name || '—'}</Badge>
                        {a.asset.phase && <Badge variant="outline" className="text-[10px]">{a.asset.phase}</Badge>}
                        <Badge className={`text-[10px] ml-auto border ${TIER_META[a.tier]?.chip}`}>{a.score}</Badge>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-1">
                        {[a.asset.therapeutic_area, a.asset.indication, a.asset.modality].filter(Boolean).join(' · ')}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {a.explain.map((e, j) => {
                          const isPass = e.verdict === 'pass';
                          const isFail = e.verdict === 'fail';
                          return (
                            <span
                              key={j}
                              className={`text-[10.5px] px-2 py-0.5 rounded-full border ${
                                isPass ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : isFail ? 'bg-rose-50 text-rose-700 border-rose-200'
                                : 'bg-slate-50 text-slate-500 border-slate-200'
                              }`}
                            >
                              {isPass ? '✓' : isFail ? '✗' : '·'} {e.reason}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StructuredFitPanel;
